// actions
import * as Actions from '../actions';
import { AppState } from '../reducers';

// services
import * as Services from '../../services/AzureFileUpload.Services';
import * as StorageServices from '../../services/StorageBrowser.Services';
import * as ProtocolServices from '../../services/MedicalProtocols.Services';
import * as ContractServices from '../../services/Contract.Services';

// functions
import {
  buildBlobFileName,
  getAzureTargetKey,
  getContractSponsorId,
  getExtension,
  getProviderLinkFileType,
  getRegisterKind,
  getTargetRouteId,
  mapCreditorInvoiceFile,
  mapOwnerFile,
  mapPaymentReceiptFile,
  mapProviderFile,
  mapServiceFile,
  mapSettlementFile,
  mapSponsorFile,
} from '../../functions/AzureFileUpload.Functions';
import { toTenantRelativePath } from '../../functions/StorageManagement.Functions';

// models
import {
  AzureFileLinkKind,
  AzureFileListLoaderKind,
  AzureFileTarget,
  AzureFileUploadRequest,
  AzureFileUploadResult,
  ProviderFileTypeEnum,
  RegisteredAzureFile,
  TargetStorageFile,
  TargetStorageFolder,
  UploadSasTokenRequest,
} from '../../models/Entities/AzureFiles/AzureFileUpload';
import { ErrorType } from '../../models/HttpError';
import { Contract } from '../../models/Entities/Contracts/Contract';
import { Protocol } from '../../models/Entities/Protocols/Protocol';
import { ServiceReceiptCount_Patch_Action } from '../actions/Service.Actions';

/** Page size used to list a target folder (Contracts accepts 1..500). */
export const AZURE_TARGET_STORAGE_PAGE_SIZE = 500;

type KnownAction = Actions.AzureFileUpload.AzureFileKnownAction | ServiceReceiptCount_Patch_Action;

/** Thunk that also returns a promise, so the drawer can track per-file progress/result locally. */
export type AzureFileThunk<TResult> = (
  dispatch: (action: KnownAction) => void,
  getState: () => AppState
) => Promise<TResult>;

const NotAuthorized: ErrorType = {
  ErrorCode: 401,
  ErrorMessage: 'Not authorized',
  Errors: [],
};

const toErrorType = (error: unknown): ErrorType => {
  if (typeof error === 'object' && error !== null && 'ErrorCode' in error) {
    return error as ErrorType;
  }
  return {
    ErrorCode: 0,
    ErrorMessage: error instanceof Error ? error.message : 'Unexpected error',
    Errors: [],
  };
};

const loadRegisteredFiles = async (
  kind: AzureFileListLoaderKind,
  target: AzureFileTarget,
  token: string
): Promise<RegisteredAzureFile[]> => {
  switch (kind) {
    case 'provider':
      return (await Services.getProviderFiles(token, target.entityId)).map(mapProviderFile);
    case 'protocolSponsor':
      return (await Services.getProtocolSponsorFiles(token, target.entityId)).map(mapSponsorFile);
    case 'settlement':
      return (await Services.getSettlementFiles(token, target.entityId)).map(mapSettlementFile);
    case 'service':
      return (await Services.getServiceFiles(token, String(getTargetRouteId(target)))).map(mapServiceFile);
    case 'paymentReceipt':
      return (await Services.getPaymentReceipts(token, target.entityId)).map(mapPaymentReceiptFile);
    case 'creditorInvoice':
      return (await Services.getCreditorInvoiceFiles(token, String(getTargetRouteId(target)))).map(mapCreditorInvoiceFile);
    default:
      return [];
  }
};

/** SAS request body; owner fields are only sent when the target has an owner (contract §2). */
export const buildSasRequest = (
  request: AzureFileUploadRequest,
  fileName: string,
  fileExtension: string
): UploadSasTokenRequest => {
  const body: UploadSasTokenRequest = {
    fileType: request.fileType,
    entityType: request.target.entityType,
    entityId: request.target.entityId,
    fileName,
    fileExtension,
  };
  // Service receipts (contract §10.1): the GUID travels as entityKey and entityId as 0.
  if (request.target.entityKey) body.entityKey = request.target.entityKey;
  const owner = request.target.owner;
  if (owner) {
    body.ownerType = owner.ownerType;
    body.ownerId = owner.ownerId;
    if (owner.protocolId !== undefined) body.protocolId = owner.protocolId;
  }
  return body;
};

/** POST .../register in the owner service; dispatches request/success/fail and rethrows the error. */
const registerInOwner = async (
  dispatch: (action: KnownAction) => void,
  token: string,
  kind: AzureFileListLoaderKind,
  target: AzureFileTarget,
  path: string,
  extra?: Record<string, unknown>
): Promise<RegisteredAzureFile | undefined> => {
  const targetKey = getAzureTargetKey(target);
  dispatch({ type: 'AZUREFILE_REGISTER_REQUEST_ACTION', targetKey, path });
  try {
    // Sin datos extra se llama igual que siempre (sólo { path }).
    const data = extra
      ? await Services.registerFile(token, kind, getTargetRouteId(target), path, extra)
      : await Services.registerFile(token, kind, getTargetRouteId(target), path);
    const file = data ? mapOwnerFile(kind, data) : undefined;
    dispatch({ type: 'AZUREFILE_REGISTER_SUCCESS_ACTION', targetKey, path, file });
    return file;
  } catch (error: unknown) {
    const httpError = toErrorType(error);
    dispatch({ type: 'AZUREFILE_REGISTER_FAIL_ACTION', targetKey, path, error: httpError });
    throw httpError;
  }
};

export const ActionCreators = {
  /**
   * Requests an upload SAS to Contracts and PUTs the binary directly to Azure Storage.
   * The binary never goes through any API. Resolves with the blob path, rejects with ErrorType.
   */
  UploadAzureFile: (
    request: AzureFileUploadRequest,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal
  ): AzureFileThunk<AzureFileUploadResult> => {
    return async (dispatch, getState) => {
      const state = getState();
      const fileName = request.file.name;

      if (state.oidc?.user?.access_token === undefined) {
        dispatch({
          type: 'AZUREFILE_UPLOAD_FAIL_ACTION',
          target: request.target,
          fileType: request.fileType,
          fileName,
          error: NotAuthorized,
        });
        throw NotAuthorized;
      }
      const token: string = state.oidc.user.access_token;

      dispatch({
        type: 'AZUREFILE_UPLOAD_REQUEST_ACTION',
        target: request.target,
        fileType: request.fileType,
        fileName,
      });

      let uploaded: AzureFileUploadResult;
      try {
        const blobFileName = buildBlobFileName(fileName);
        const fileExtension = getExtension(fileName);

        const sas = await Services.requestUploadSas(
          token,
          buildSasRequest(request, blobFileName, fileExtension)
        );

        await Services.uploadToAzure(sas.sasUrl, request.file, onProgress, signal);

        dispatch({
          type: 'AZUREFILE_UPLOAD_SUCCESS_ACTION',
          target: request.target,
          fileType: request.fileType,
          fileName,
          blobPath: sas.blobPath,
        });
        uploaded = { blobPath: sas.blobPath, fileName: blobFileName, fileExtension };
      } catch (error: unknown) {
        const httpError = toErrorType(error);
        dispatch({
          type: 'AZUREFILE_UPLOAD_FAIL_ACTION',
          target: request.target,
          fileType: request.fileType,
          fileName,
          error: httpError,
        });
        throw httpError;
      }

      // The storage event may never arrive: register the file in its owner service right away (contract §9.5).
      // A failed register does not undo the upload: the result says so and the drawer offers a retry.
      const relativePath = toTenantRelativePath(uploaded.blobPath);
      const kind = getRegisterKind(request.fileType);
      try {
        await registerInOwner(dispatch, token, kind, request.target, relativePath, request.registerExtra);
        return { ...uploaded, relativePath, registered: true };
      } catch (error: unknown) {
        return { ...uploaded, relativePath, registered: false, registerError: toErrorType(error) };
      }
    };
  },

  /** Retries only the register call of an already uploaded blob. Rejects with ErrorType. */
  RegisterAzureFile: (
    kind: AzureFileListLoaderKind,
    target: AzureFileTarget,
    path: string,
    extra?: Record<string, unknown>
  ): AzureFileThunk<RegisteredAzureFile | undefined> => {
    return async (dispatch, getState) => {
      const token = getState().oidc?.user?.access_token;
      if (token === undefined) {
        dispatch({ type: 'AZUREFILE_REGISTER_FAIL_ACTION', targetKey: getAzureTargetKey(target), path, error: NotAuthorized });
        throw NotAuthorized;
      }
      return registerInOwner(dispatch, token, kind, target, path, extra);
    };
  },

  /**
   * Links an existing storage file to a settlement, a protocol or a service (contract §9.3 / §10.3). The protocol
   * and service links send the sponsor of the target owner. Rejects with ErrorType (409 = already linked).
   */
  LinkAzureFile: (
    kind: AzureFileLinkKind,
    target: AzureFileTarget,
    path: string,
    /** Provider links only (spec 005): MedicalProtocols file type; inferred from the extension when omitted. */
    fileType?: ProviderFileTypeEnum
  ): AzureFileThunk<RegisteredAzureFile | undefined> => {
    return async (dispatch, getState) => {
      const targetKey = getAzureTargetKey(target);
      const token = getState().oidc?.user?.access_token;
      if (token === undefined) {
        dispatch({ type: 'AZUREFILE_LINK_FAIL_ACTION', targetKey, path, error: NotAuthorized });
        throw NotAuthorized;
      }
      dispatch({ type: 'AZUREFILE_LINK_REQUEST_ACTION', targetKey, path });
      try {
        const sponsorId = kind === 'protocolSponsor' || kind === 'service' ? Number(target.owner?.ownerId) : undefined;
        const data =
          kind === 'provider'
            ? await Services.linkFile(token, kind, getTargetRouteId(target), path, undefined, fileType ?? getProviderLinkFileType(path))
            : await Services.linkFile(token, kind, getTargetRouteId(target), path, sponsorId);
        const file = data ? mapOwnerFile(kind, data) : undefined;
        dispatch({ type: 'AZUREFILE_LINK_SUCCESS_ACTION', targetKey, path, file });
        return file;
      } catch (error: unknown) {
        const httpError = toErrorType(error);
        dispatch({ type: 'AZUREFILE_LINK_FAIL_ACTION', targetKey, path, error: httpError });
        throw httpError;
      }
    };
  },

  /** Removes a link (logical delete of the record; the blob is never deleted). Rejects with ErrorType. */
  UnlinkAzureFile: (
    kind: AzureFileLinkKind,
    target: AzureFileTarget,
    fileId: string
  ): AzureFileThunk<void> => {
    return async (dispatch, getState) => {
      const targetKey = getAzureTargetKey(target);
      const token = getState().oidc?.user?.access_token;
      if (token === undefined) {
        dispatch({ type: 'AZUREFILE_UNLINK_FAIL_ACTION', targetKey, fileId, error: NotAuthorized });
        throw NotAuthorized;
      }
      dispatch({ type: 'AZUREFILE_UNLINK_REQUEST_ACTION', targetKey, fileId });
      try {
        await Services.unlinkFile(token, kind, getTargetRouteId(target), fileId);
        dispatch({ type: 'AZUREFILE_UNLINK_SUCCESS_ACTION', targetKey, fileId });
      } catch (error: unknown) {
        const httpError = toErrorType(error);
        dispatch({ type: 'AZUREFILE_UNLINK_FAIL_ACTION', targetKey, fileId, error: httpError });
        throw httpError;
      }
    };
  },

  /** Loads the files already registered by the owner service for the target. Never rejects. */
  GetRegisteredAzureFiles: (kind: AzureFileListLoaderKind, target: AzureFileTarget): AzureFileThunk<void> => {
    return async (dispatch, getState) => {
      const state = getState();
      const targetKey = getAzureTargetKey(target);

      if (state.oidc?.user?.access_token === undefined) {
        dispatch({ type: 'AZUREFILE_GETREGISTERED_FAIL_ACTION', targetKey, error: NotAuthorized });
        return;
      }
      const token: string = state.oidc.user.access_token;

      dispatch({ type: 'AZUREFILE_GETREGISTERED_REQUEST_ACTION', targetKey });

      try {
        const files = await loadRegisteredFiles(kind, target, token);
        dispatch({ type: 'AZUREFILE_GETREGISTERED_RECEIVE_ACTION', targetKey, files });
        // The services list shows the receipt count: keep it in sync with what the drawer just read.
        if (kind === 'service' && target.entityKey) {
          dispatch({ type: 'SERVICE_RECEIPTCOUNT_PATCH_ACTION', ServiceId: target.entityKey, receiptCount: files.length });
        }
      } catch (error: unknown) {
        dispatch({ type: 'AZUREFILE_GETREGISTERED_FAIL_ACTION', targetKey, error: toErrorType(error) });
      }
    };
  },

  /**
   * Lists the files of the target straight from the storage (source of truth), browsing every
   * folder where Contracts puts them (one per file type, contract §2). A folder that does not
   * exist yet comes back empty. Never rejects.
   */
  GetTargetStorageFiles: (target: AzureFileTarget, folders: TargetStorageFolder[]): AzureFileThunk<void> => {
    return async (dispatch, getState) => {
      const state = getState();
      const targetKey = getAzureTargetKey(target);

      if (state.oidc?.user?.access_token === undefined) {
        dispatch({ type: 'AZUREFILE_GETSTORAGE_FAIL_ACTION', targetKey, error: NotAuthorized });
        return;
      }
      const token: string = state.oidc.user.access_token;

      dispatch({ type: 'AZUREFILE_GETSTORAGE_REQUEST_ACTION', targetKey });

      try {
        const listings = await Promise.all(
          folders.map(async (folder) => {
            const result = await StorageServices.browseStorage(token, {
              path: folder.path,
              pageSize: AZURE_TARGET_STORAGE_PAGE_SIZE,
            });
            return result.files.map((file): TargetStorageFile => ({ fileType: folder.fileType, file }));
          })
        );
        dispatch({ type: 'AZUREFILE_GETSTORAGE_RECEIVE_ACTION', targetKey, files: listings.flat() });
      } catch (error: unknown) {
        dispatch({ type: 'AZUREFILE_GETSTORAGE_FAIL_ACTION', targetKey, error: toErrorType(error) });
      }
    };
  },

  /**
   * Sponsor of a protocol: owner of the service receipts folder (contract §10.2). Same rule as the protocol
   * consumptions upload: first sponsor of the protocol contract (protocol.contractAssociation → contract.sponsors).
   * Uses the protocol / contract already in the store and only reads what is missing. Cached per protocol.
   * Never rejects: resolves with the sponsor id, or undefined when there is none or it failed.
   */
  ResolveProtocolSponsor: (protocolId: number): AzureFileThunk<number | undefined> => {
    return async (dispatch, getState) => {
      if (!Number.isInteger(protocolId) || protocolId <= 0) return undefined;
      const state = getState();
      const cached = state.AzureFiles?.protocolSponsors?.[protocolId];
      if (cached?.status === 'loaded' || cached?.status === 'loading') return cached.sponsorId;

      const protocol = state.MedicalProtocols?.list?.find((item: Protocol) => item.id === protocolId);
      const knownContractId = protocol?.contractAssociation?.contractId;
      const knownContract = knownContractId
        ? state.Contract?.list?.find((item: Contract) => item.id === knownContractId)
        : undefined;
      const knownSponsorId = getContractSponsorId(knownContract);
      if (knownSponsorId !== undefined) {
        dispatch({ type: 'AZUREFILE_PROTOCOLSPONSOR_RECEIVE_ACTION', protocolId, sponsorId: knownSponsorId });
        return knownSponsorId;
      }

      const token = state.oidc?.user?.access_token;
      if (token === undefined) {
        dispatch({ type: 'AZUREFILE_PROTOCOLSPONSOR_FAIL_ACTION', protocolId, error: NotAuthorized });
        return undefined;
      }

      dispatch({ type: 'AZUREFILE_PROTOCOLSPONSOR_REQUEST_ACTION', protocolId });
      try {
        const contractId =
          knownContractId ?? (await ProtocolServices.GetProtocolContracts(protocolId, token))?.contractId;
        const contract = contractId ? await ContractServices.GetContractById(contractId, token) : undefined;
        const sponsorId = getContractSponsorId(contract);
        dispatch({ type: 'AZUREFILE_PROTOCOLSPONSOR_RECEIVE_ACTION', protocolId, sponsorId });
        return sponsorId;
      } catch (error: unknown) {
        dispatch({ type: 'AZUREFILE_PROTOCOLSPONSOR_FAIL_ACTION', protocolId, error: toErrorType(error) });
        return undefined;
      }
    };
  },
};
